import { action, computed, observable, reaction, when } from 'mobx';
import { RouterStore } from '@estee/elc-router';
import {
  SHIPPING_FORM_ERROR,
  SHIPPING_FORM_ERROR_SELECTED,
  BILLING_FORM_ERROR,
  BILLING_FORM_ERROR_SELECTED,
  COMPANY_BILLING_FORM_ERROR,
  PACKSTATION_FORM_ERROR,
  GIFTWRAP_ERROR,
} from '../../use-cases/ValidateAndSubmitAddressesUseCase';
import SubmitDeliveryScreenUseCase from '../../use-cases/SubmitDeliveryScreenUseCase';
import SubmitBopisUseCase, {
  PURCHASER_FORM_ERROR,
  PROXY_FORM_ERROR,
} from '../../use-cases/SubmitBopisUseCase';
import ToggleSubscriptionUseCase from '../../use-cases/ToggleSubscriptionUseCase';
import SubmitCourierUseCase from '../../use-cases/SubmitCourierUseCase';
import SaveCourierAddressUseCase from '../../use-cases/SaveCourierAddressUseCase';
import UserPreferencesRepository from '../../repositories/UserPreferencesRepository';
import Status from '../../constants/Status';
import ShippingRepository from '../../repositories/ShippingRepository';
import TransactionRepository from '../../repositories/TransactionRepository';
import UiState from '../../stores/UIState';
import ConfigRepository from '../../repositories/ConfigRepository';
import Config from '../../domain/entities/Config/Config';
import Transaction, { AvailableDeliveryOptionsMap, IDeliveryOptionConfig } from '../../domain/entities/Transaction/Transaction';
import Constants, {
  TIMEOUT_INTERVAL, ADDRESS_TYPE, DeliveryOptions, ADDRESS_TYPE_ID, STORE_TYPE, NAVIGATION_ITEM_IDS, FIELD_TYPE,
} from '../../constants/Constants';
import { AddressId, IAddressAttributes } from '../../domain/entities/Address';
import { ADDRESS, RESTRICTION_ERRORS, GIFT_MESSAGE } from '../../constants/Errors';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import formatPrice from '../../utils/PriceFormatUtils';
import { IHours } from '../../domain/entities/Store/CourierStoresCollection';
import SHIPPING_METHODS from '../../constants/ShippingMethods';
import { scrollToTargets } from '../../utils/ScrollTo';
import Store from '../../domain/entities/Store/Store';
import CartItem from '../../domain/entities/Cart/CartItem';
import { EDIT_CART_OPTION } from '../../views/BopisView/EditCart';
import { DELIVERY, PAYMENT } from '../../routes';
import LoadStoresService from '../../application/services/LoadStoresService';
import AnalyticsRepository from '../../repositories/AnalyticsRepository';
import UniqueDomIdentifiers from '../../constants/UniqueDomIdentifiers';
import { ShippingMethodsCollection } from '../../domain/entities/ShippingMethodsCollection/ShippingMethodsCollection';
import ObjectUtils from '../../utils/ObjectUtils';
import SelectDeliveryOptionUseCase from '../../use-cases/SelectDeliveryOptionUseCase';
import BopisStoresRepository from '../../repositories/BopisStoresRepository';
import AUPostCollectionsStoreRepository from '../../repositories/AUPostCollectionsStoreRepository';
import CourierStoresRepository from '../../repositories/CourierStoresRepository';
import SubmitConvenienceStoreUseCase from '../../use-cases/SubmitConvenienceStoreUseCase';
import SubmitAUPostCollectionsUseCase from '../../use-cases/SubmitAUPostCollectionsUseCase';
import SetClickAndCollectStoreUseCase from '../../use-cases/SetClickAndCollectStoreUseCase';
import ShippingUIState from '../../stores/UIState/ShippingUIState';
import ZeroPaymentUseCase from '../../use-cases/ZeroPaymentUseCase';
import Routes from '../../application/routes/Routes';
import GiftWrapRepository from '../../repositories/GiftWrapRepository';
import SubmitGiftMessageUseCase from '../../use-cases/SubmitGiftMessageUseCase';
import normalizeApiErrors from '../../helpers/normalizeApiErrors';
import { IPriceWithTaxConfig } from '../../utils/ShippingMethodUtils';
import BillingRepository from '../../repositories/BillingRepository';
import { IFromEstimatedDeliveryMapping } from '../../api/IDataMappings';
import { IUpsIndicator } from '../../api/IApiResponses';
import { CART_NOTIFICATIONS, NOTIFICATION_GROUP, NOTIFICATION_TYPE } from '../../constants/Notifications';
import EditShippingAddressUseCase from '../../use-cases/EditShippingAddressUseCase';

export interface IDeliveryScreenViewControllerData {
  availableDeliveryOptions: AvailableDeliveryOptionsMap;
  deliveryOption: DeliveryOptions;
  deliveryOptionConfig: IDeliveryOptionConfig;
  isBeforeOpening: boolean;
  isDuringHours: boolean;
  isAfterHours: boolean;
  isLoading: boolean;
  isContinueButtonDisabled: boolean;
  isContinueButtonLoading: boolean;
  isContinueButtonVisible: boolean;
  shouldShowCourierError: boolean;
  isMobile: boolean;
  isShippingDisabled: boolean;
  isSubscriptionSelected: boolean;
  isTransactionLoaded: boolean;
  sameDayLastOrder: number;
  status: Status;
  userAddress: IAddressAttributes;
  upsAddress: IAddressAttributes[];
  upsIndicators: IUpsIndicator[];
  isSalonCommissionVisible: boolean;
  currentTime: number;
  selectedCourierTime: string;
  todaysHoursArray: IHours[];
  tomorrowsHoursArray: IHours[];
  dropoffNotes: string;
  isUserSignedIn: boolean;
  todayDate: string;
  nextBusinessDayDate: string;
  courierStore: Store;
  cartItems: CartItem[];
  baseUrl: string;
  packstationEnabled: boolean;
  priceConfig: IPriceWithTaxConfig;
  showNewsletter: boolean;
  isEditCartBtnLoading: boolean;
  isEngravedProductInCart: boolean;
  isStoreWithinRadius: boolean;
  isInventoryLoading: boolean;
  showGiftWrapMessage: boolean;
  canSetCompanyBilling: boolean;
  shippingOptions: ShippingMethodsCollection;
  isSampleInCart: boolean;
  shouldRenderTabs: boolean;
  selectedNamedDeliveryDate: string;
  selectedNamedDeliveryTime: string;
  isDeliveryMethodLoading: boolean;
  privacyPolicyUrl: string;
  isElectronicShipping: boolean;
  isBillingVisible: boolean;
  isLastCheckoutStep: boolean;
  deliveryTabsDisabled: boolean;
  isGiftWrapEnabled: boolean;
  isGiftWrapItemEnabled: boolean;
  isGiftWrapMessageEnabled: boolean;
  isGiftWrapLoading: boolean;
  isGiftWrapMessageLoading: boolean;
  shouldEnableAUPostDeliveryOption: boolean;
  isEstimatedDeliveryDateEnabled: boolean;
  showSignatureRequiredMessage: boolean;
}

export interface IDeliveryScreenViewControllerConfig {
  userPreferencesRepository: UserPreferencesRepository;
  shippingRepository: ShippingRepository;
  billingRepository: BillingRepository;
  transactionRepository: TransactionRepository;
  configRepository: ConfigRepository;
  uiState: UiState;
  shippingUIState: ShippingUIState;
  stateRouter: RouterStore;
  bopisStoresRepository: BopisStoresRepository;
  auPostCollectionsStoreRepository: AUPostCollectionsStoreRepository;
  courierStoresRepository: CourierStoresRepository;
  notificationsCollection: NotificationsCollection;
  loadStoresService: LoadStoresService;
  analyticsRepository: AnalyticsRepository;
  routes: Routes;
  giftWrapRepository: GiftWrapRepository;
}

// Custom clients side form errors with mapping selectors
export const FORM_ERROR_SELECTORS: { [key: string]: string } = {
  [BILLING_FORM_ERROR]: `[data-test-id="${ADDRESS_TYPE.BILLING}-form"]`,
  [BILLING_FORM_ERROR_SELECTED]: `[data-test-id="${ADDRESS_TYPE.BILLING}-form"]`,
  [COMPANY_BILLING_FORM_ERROR]: `[data-test-id="${ADDRESS_TYPE.COMPANY_BILLING}-form"]`,
  [GIFTWRAP_ERROR]: `[data-test-id="${UniqueDomIdentifiers.giftWrap}"]`,
  [PACKSTATION_FORM_ERROR]: `[data-test-id="${ADDRESS_TYPE.PACKSTATION}-form"]`,
  [PROXY_FORM_ERROR]: `[data-test-id="${UniqueDomIdentifiers.preferredStoreViewWhoPickup}"]`,
  [PURCHASER_FORM_ERROR]: `[data-test-id="${UniqueDomIdentifiers.preferredStoreViewContactInfo}"]`,
  [SHIPPING_FORM_ERROR]: `[data-test-id="${ADDRESS_TYPE.SHIPPING}-form"]`,
  [SHIPPING_FORM_ERROR_SELECTED]: `[data-addressitem-selected-type="${ADDRESS_TYPE.SHIPPING}"]`,
};

class DeliveryScreenViewController {
  @observable config: Config;

  @observable isUserSignedIn: boolean;

  @observable deliveryStatus: Status = Status.IDLE;

  @observable transaction: Transaction;

  @observable private deliveryOption: DeliveryOptions;

  @observable notificationsCollection: NotificationsCollection;

  @observable _selectedCourierTime: string;

  @observable dropoffNotes: string;

  @observable isEditCartBtnLoading = false;

  @observable shippingUIState: ShippingUIState;

  @observable isDeliveryMethodLoading = false;

  @observable removeUnavailableSelected = false;

  @observable shouldEnableAUPostDeliveryOption = true;

  private userPreferencesRepository: UserPreferencesRepository;

  private giftWrapRepository: GiftWrapRepository;

  private shippingRepository: ShippingRepository;

  private billingRepository: BillingRepository;

  private transactionRepository: TransactionRepository;

  private configRepository: ConfigRepository;

  private uiState: UiState;

  private stateRouter: RouterStore;

  private bopisStoresRepository: BopisStoresRepository;

  private auPostCollectionsStoreRepository: AUPostCollectionsStoreRepository;

  private courierStoresRepository: CourierStoresRepository;

  private loadStoresService: LoadStoresService;

  private analyticsRepository: AnalyticsRepository;

  private routes: Routes;

  public disposers: (() => void)[];

  constructor(config: IDeliveryScreenViewControllerConfig) {
    this.userPreferencesRepository = config.userPreferencesRepository;
    this.shippingRepository = config.shippingRepository;
    this.billingRepository = config.billingRepository;
    this.transactionRepository = config.transactionRepository;
    this.configRepository = config.configRepository;
    this.uiState = config.uiState;
    this.stateRouter = config.stateRouter;
    this.loadStoresService = config.loadStoresService;
    this.bopisStoresRepository = config.bopisStoresRepository;
    this.auPostCollectionsStoreRepository = config.auPostCollectionsStoreRepository;
    this.courierStoresRepository = config.courierStoresRepository;
    this.notificationsCollection = config.notificationsCollection;
    this.analyticsRepository = config.analyticsRepository;
    this.shippingUIState = config.shippingUIState;
    this.routes = config.routes;
    this.giftWrapRepository = config.giftWrapRepository;
    this.disposers = [];
  }

  @action
  async load(): Promise<void> {
    this.analyticsRepository.intialAnalyticsPageView();
    this.analyticsRepository.shippingScreenViewEvent();
    this.analyticsRepository.enterShippingAddressLinkEvent();

    this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.PAYMENT, NOTIFICATION_TYPE.DANGER);
    this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.SHIPPING_PAYMENT, NOTIFICATION_TYPE.WARNING);
    const config = await this.configRepository.getConfig();
    this.setConfig(config);

    if (this.deliveryStatus === Status.LOADED) {
      // mark it idle again
      this.setDeliveryStatus(Status.IDLE);
    }

    const transaction = await this.transactionRepository.getTransaction();
    this.setTransaction(transaction);
    this.setIsUserSignedIn(this.transaction.user.isUserRegistered);
    if (this.config.newsletter) {
      await this.userPreferencesRepository.loadSubscription();
    }

    this.setSelectedDeliveryOption();

    this.getCourierStoreHours();
    this.updateEstimatedDeliveryDate();

    const { courierStoresCollection } = this.courierStoresRepository;

    reaction(() => courierStoresCollection?.shouldShowCourierError,
      (shouldShowCourierError): void => {
        if (shouldShowCourierError) {
          scrollToTargets('#notification-messages-container');
        }
      });

    // listening to country change and form editing change to 
    // update AUPost delivery option enablement
    this.setEnableAUPostDeliveryOption();
    const disposerFormCountryReaction = reaction(() => this.shippingRepository.formStore.values[FIELD_TYPE.COUNTRY_ID],
      this.setEnableAUPostDeliveryOption);
    const disposerTransactionCountryReaction = reaction(() => this.transaction.shippingAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID],
      this.setEnableAUPostDeliveryOption);
    const disposerIsEditingReaction = reaction(() => this.shippingRepository?.isEditing,
      this.setEnableAUPostDeliveryOption);

    this.disposers.push(disposerFormCountryReaction);
    this.disposers.push(disposerTransactionCountryReaction);
    this.disposers.push(disposerIsEditingReaction);

    reaction(() => this.stateRouter.currentRoute,
      currentRoute => {
        if (currentRoute.includes(PAYMENT.ROUTE) && !this.isPaymentReady) {
          this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE);
        }
      });
    reaction(() => this.transaction.selectedShippingMethodId,
      () => {
        if (!this.isDeliveryMethodLoading) {
          this.setDeliveryOption(this.transaction.deliveryOption);
        }
      });
    when(() => !this.transaction.deliveryTabsDisabled, this.getCourierStoreHours);
  }

  @action
  private setConfig(config: Config): void {
    this.config = config;
  }

  @action
  private setTransaction(transaction: Transaction): void {
    this.transaction = transaction;
  }

  @computed
  private get inEditMode(): boolean {
    return !!(this.isUserSignedIn && !!Number(this.stateRouter?.queryParams?.inEditMode));
  }

  @computed
  private get shouldRenderTabs(): boolean {
    return !this.transaction?.deliveryTabsDisabled
      && ObjectUtils.countTruthyFields(this.availableDeliveryOptions) > 1;
  }

  @computed
  get showSignatureRequiredMessage(): boolean {
    return this.notificationsCollection?.hasNotification(CART_NOTIFICATIONS.SIGNATURE_REQUIRED);
  }

  @action
  public setEnableAUPostDeliveryOption = (): void => {
    const auPostDeliveryOption = this.transaction?.deliveryOptions?.find(option => option.name === DeliveryOptions.AU_POST_COLLECTIONS);
    const auPostAllowedCountries = auPostDeliveryOption?.includedCountries;

    // if has auPostAllowedCountries config then only countries included in the list can enable AU Post delivery option
    if (auPostAllowedCountries) {
      // get user selected countryId from form store or from transaction
      let countryId;
      // get countryId from form store
      const {
        isEditing,
      } = this.shippingRepository;
      const guestUserOrHasNoAddress = !this.isUserSignedIn || !Boolean(this.shippingRepository?.addresses?.length);
      const inEditingMode = isEditing || this.transaction.shippingAddress.isNewAddress || guestUserOrHasNoAddress;
      if (inEditingMode) {
        countryId = this.shippingRepository.formStore.values[FIELD_TYPE.COUNTRY_ID];
      } else {
        countryId = this.transaction?.shippingAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID];
      }
      if (countryId) {
        this.shouldEnableAUPostDeliveryOption = auPostAllowedCountries.includes(countryId);
      }
    }
  };

  @computed
  private get isGiftWrapEnabled(): boolean {
    return (this.transaction?.giftWrapSettings?.giftWrapEnabled
      || this.transaction?.giftMessageSettings?.giftMessageEnabled)
    && !this.config.isGiftWrapAndDonationsHidingExperimentEnabled;
  }

  @computed
  public get data(): IDeliveryScreenViewControllerData {
    const {
      userPreferencesRepository: {
        userPreferences,
      },
      config,
    } = this;

    const { courierStoresCollection } = this.courierStoresRepository;

    return {
      availableDeliveryOptions: this.availableDeliveryOptions,
      baseUrl: this.config?.baseUrl,
      canSetCompanyBilling: this.configRepository.config?.canSetCompanyBilling,
      cartItems: this.transaction?.cart.cartItems,
      courierStore: courierStoresCollection.courierStore,
      currentTime: courierStoresCollection.currentTime,
      deliveryOption: this.deliveryOption,
      deliveryOptionConfig: this.deliveryOptionConfig,
      deliveryTabsDisabled: this.transaction?.deliveryTabsDisabled,
      dropoffNotes: this.dropoffNotes,
      isAfterHours: courierStoresCollection.isAfterHours,
      isBeforeOpening: courierStoresCollection.isBeforeOpening,
      isBillingVisible: !this.transaction?.isZeroPaymentCheckout,
      isContinueButtonDisabled: this.isContinueButtonDisabled,
      isContinueButtonLoading: this.isContinueButtonLoading,
      isContinueButtonVisible: this.isContinueButtonVisible,
      isDeliveryMethodLoading: this.isDeliveryMethodLoading,
      isDuringHours: courierStoresCollection.isDuringHours,
      isEditCartBtnLoading: this.isEditCartBtnLoading,
      isElectronicShipping: this.transaction?.isElectronicShipping,
      isEngravedProductInCart: this.isEngravedProductInCart,
      isEstimatedDeliveryDateEnabled: this.config?.isEstimatedDeliveryDateEnabled,
      isGiftWrapEnabled: this.isGiftWrapEnabled,
      isGiftWrapItemEnabled: this.transaction?.giftWrapSettings?.giftWrapEnabled,
      isGiftWrapLoading: this.giftWrapRepository?.isGiftWrapLoading,
      isGiftWrapMessageEnabled: this.transaction?.giftMessageSettings?.giftMessageEnabled,
      isGiftWrapMessageLoading: this.giftWrapRepository?.isGiftMessageLoading,
      isInventoryLoading: this.courierStoresRepository.isInventoryLoading,
      isLastCheckoutStep: this.isZeroPaymentCheckout,
      isLoading: this.isLoading,
      isMobile: this.isMobile,
      isSalonCommissionVisible: this.isSalonCommissionVisible,
      isSampleInCart: this.transaction?.cart.isSampleInCart,
      isShippingDisabled: this.isShippingDisabled,
      isStoreWithinRadius: courierStoresCollection.isStoreWithinRadius,
      isSubscriptionSelected:
        userPreferences
          ? userPreferences.subscribed
          : false,
      isTransactionLoaded: this.transaction?.isTransactionLoaded,
      isUserSignedIn: this.isUserSignedIn,
      nextBusinessDayDate: courierStoresCollection.nextBusinessDayDate,
      packstationEnabled: true,
      priceConfig: { displayShippingFeeWithTax: config?.displayShippingFeeWithTax, vatPercentage: config?.vatPercentage },
      privacyPolicyUrl: this.config?.privacyPolicyUrl,
      sameDayLastOrder: courierStoresCollection.sameDayLastOrder,
      selectedCourierTime: this.selectedCourierTime,
      selectedNamedDeliveryDate: this.transaction?.namedDelivery?.deliveryDate,
      selectedNamedDeliveryTime: this.transaction?.namedDelivery?.deliveryTime,
      shippingOptions: this.transaction?.shippingOptions,
      shouldEnableAUPostDeliveryOption: this.shouldEnableAUPostDeliveryOption,
      shouldRenderTabs: this.shouldRenderTabs,
      shouldShowCourierError: courierStoresCollection.shouldShowCourierError
        && !this.courierStoresRepository.isInventoryLoading,
      showGiftWrapMessage: !this.config?.isClickAndCollect && this.showGiftWrapMessage,
      showNewsletter: this.config?.newsletter,
      showSignatureRequiredMessage: this.showSignatureRequiredMessage,
      status: this.status,
      todayDate: courierStoresCollection.todayDate,
      todaysHoursArray: courierStoresCollection.getHoursArray('today'),
      tomorrowsHoursArray: courierStoresCollection.getHoursArray('tomorrow'),
      upsAddress: this.shippingRepository.upsAddress,
      upsIndicators: this.shippingRepository.upsIndicators,
      userAddress: this.shippingRepository.userAddress,
    };
  }

  public toggleSubscription(): void {
    const useCase = new ToggleSubscriptionUseCase();
    useCase.execute();
  }

  @action
  public onSubmit = async (
    isBillingSameAsShipping: boolean,
    withUpsValidation: boolean,
  ): Promise<void> => {
    try {
      if (this.deliveryStatus === Status.LOADING) {
        return;
      }

      if (this.transaction?.giftMessageSettings?.giftMessageEnabled) {
        await new SubmitGiftMessageUseCase().execute();
      }
      
      this.setDeliveryStatus(Status.LOADING);

      switch (this.deliveryOption) {
        case DeliveryOptions.CLICK_AND_COLLECT:
          await new SubmitBopisUseCase().execute();
          break;
        case DeliveryOptions.SAME_DAY_DELIVERY:
          if (this.transaction?.deliveryTabsDisabled) {
            await this.submitDeliveryDefault(isBillingSameAsShipping, withUpsValidation);
          } else {
            await this.submitCourier(isBillingSameAsShipping, withUpsValidation);
          }
          break;
        case DeliveryOptions.CONVENIENCE_STORE_PICK_UP:
          await new SubmitConvenienceStoreUseCase().execute();
          break;
        case DeliveryOptions.AU_POST_COLLECTIONS:
          await new SubmitAUPostCollectionsUseCase().execute();
          break;
        case DeliveryOptions.UPS_STORE_PICK_UP:
          await new SetClickAndCollectStoreUseCase().execute();
          break;
        default:
          await this.submitDeliveryDefault(isBillingSameAsShipping, withUpsValidation);
      }

      if (this.isZeroPaymentCheckout) {
        await new ZeroPaymentUseCase().execute();
      } else if (this.transaction?.deliveryTabsDisabled ||
          !(this.deliveryOption === DeliveryOptions.SAME_DAY_DELIVERY)) {
        this.stateRouter.changeCurrentRoute(PAYMENT.ROUTE);
        this.setDeliveryStatus(Status.LOADED);
      }
    } catch (error) {
      this.handleAddressSubmissionErrors(error);
    }
  };

  @action
  public submitDeliveryDefault = async (
    isBillingSameAsShipping: boolean,
    withUpsValidation: boolean,
  ): Promise<void> => {
    await new SubmitDeliveryScreenUseCase(
      {
        backendValidation: this.config.backendValidation,
        deliveryOption: this.deliveryOption,
        isBillingSameAsShipping: (this.deliveryOption !== DeliveryOptions.PACKSTATION)
          && isBillingSameAsShipping,
        withUpsValidation: this.config.useUpsValidation && withUpsValidation,
      },
    ).execute();
  };

  public handleAddressSubmissionErrors = (error: Error, shouldThrowHandledError = true): void => {
    const { message } = error;
    const normalizedError = normalizeApiErrors(error);

    const scrollTo: string = FORM_ERROR_SELECTORS[message];
    if (scrollTo) {
      setTimeout(() => {
        scrollToTargets(scrollTo);
      }, TIMEOUT_INTERVAL.TEN);
    }

    if (normalizedError[0].key === GIFT_MESSAGE.PROFANIFY_FILTER) {
      this.notificationsCollection.addNotification({
        group: NOTIFICATION_GROUP.GIFT_WRAP_MESSAGE,
        key: GIFT_MESSAGE.PROFANIFY_FILTER,
      });
      this.giftWrapRepository.setIsGiftWrapMessageLoading(false);
    }

    if (message === SHIPPING_FORM_ERROR_SELECTED || message === ADDRESS.INVALID_AUPOST_QAS_ADDRESS) {
      this.stateRouter.changeCurrentRoute(
        DELIVERY.ROUTE, {}, { inEditMode: 1 },
      );
      this.notificationsCollection.addNotification({
        group: NOTIFICATION_GROUP.SHIPPING,
        key: SHIPPING_FORM_ERROR_SELECTED,
      });
    }
    if (message === BILLING_FORM_ERROR_SELECTED) {
      this.notificationsCollection.addNotification({
        group: NOTIFICATION_GROUP.BILLING,
        key: BILLING_FORM_ERROR_SELECTED,
      });
    }
    if (message !== Constants.UPS_VALIDATION || !scrollTo) {
      this.setDeliveryStatus(Status.ERROR);
    }
    if (message && !this.isPaymentReady) {
      this.uiState.updateItemActiveState(NAVIGATION_ITEM_IDS.paymentId, false);
    }

    if (shouldThrowHandledError || !FORM_ERROR_SELECTORS[message]) {
      throw error;
    }
  };

  @action
  public setDeliveryStatus = (status: Status): void => {
    this.deliveryStatus = status;
  };

  @computed
  private get isZeroPaymentCheckout(): boolean {
    return this.transaction?.isZeroPaymentCheckout;
  }

  @computed
  private get isLoading(): boolean {
    return this.deliveryStatus === Status.LOADING;
  }

  @computed
  private get isContinueButtonDisabled(): boolean {
    if (
      [
        DeliveryOptions.CLICK_AND_COLLECT,
        DeliveryOptions.PACKSTATION,
        DeliveryOptions.AU_POST_COLLECTIONS,
        DeliveryOptions.UPS_STORE_PICK_UP,
      ].includes(this.deliveryOption)
    ) {
      return this.isContinueButtonLoading
        || this.notificationsCollection.isAborted || !this.shippingUIState.continueButtonIsEnabled;
    }
    return this.isShippingDisabled
      || this.isContinueButtonLoading
      || this.notificationsCollection.isAborted || !this.shippingUIState.continueButtonIsEnabled;
  }

  @computed
  private get isContinueButtonLoading(): boolean {
    return this.isLoading || this.shippingUIState.continueButtonIsLoading 
    || this.giftWrapRepository?.isGiftWrapLoading || this.giftWrapRepository.isGiftMessageLoading;
  }

  @computed
  private get isShippingDisabled(): boolean {
    if (this.stateRouter && this.inEditMode) {
      return true;
    }

    const { courierStoresCollection } = this.courierStoresRepository;
    if (!this.transaction?.deliveryTabsDisabled &&
      this.deliveryOption === DeliveryOptions.SAME_DAY_DELIVERY &&
      courierStoresCollection?.shouldShowCourierError) {
      return true;
    }

    if (this.isUserSignedIn
      && this.transaction
      && !this.transaction.shippingAddress.isNewAddress) {
      return this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.SHIPPING_RESTRICTIONS_STATES)
      || this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_ZIPCODE)
      || (this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS)
      || this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_MILITARY)
      || this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_TERRITORY)
      || this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_POBOX))
      || this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_OVERSEAS_TERRITORY)
      || this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.SHIPPING_UNAVAILABLE_FOR_ADDRESS);
    }

    return false;
  }

  @computed
  private get isEngravedProductInCart(): boolean {
    return this.transactionRepository.transaction?.cart.cartItems.some(item => item.isMonogrammedItem);
  }

  @computed
  public get status(): Status {
    return this.userPreferencesRepository.status;
  }

  @computed
  public get isMobile(): boolean {
    return this.uiState.isMobile;
  }

  @computed get isContinueButtonVisible(): boolean {
    if (this.deliveryOption === DeliveryOptions.CLICK_AND_COLLECT || this.deliveryOption === DeliveryOptions.UPS_STORE_PICK_UP) {
      return this.bopisStoresRepository.bopisFormData.isReady;
    }
    if (this.deliveryOption === DeliveryOptions.CONVENIENCE_STORE_PICK_UP) {
      return this.transaction?.convenienceStore?.isReady;
    }
    if (this.deliveryOption === DeliveryOptions.AU_POST_COLLECTIONS) {
      return this.auPostCollectionsStoreRepository.isFormReady;
    }

    return true;
  }

  @computed
  get availableDeliveryOptions(): AvailableDeliveryOptionsMap {
    const shipOptions = this.transaction?.availableShippingOptions;
    const deliveryOptions = this.transactionRepository?.availableDeliveryOptions;
    if (shipOptions || deliveryOptions) {
      return { ...shipOptions, ...deliveryOptions };
    }
    return { [DeliveryOptions.HOME_DELIVERY]: true };
  }

  @computed
  get deliveryOptionConfig(): IDeliveryOptionConfig {
    return this.transaction?.deliveryOptions?.find(
      deliveryOption => deliveryOption.name === this.deliveryOption,
    )?.config;
  }

  @action
  setSelectedDeliveryOption = (): void => {
    const { selectedShippingMethodId } = this.transaction;

    if (
      selectedShippingMethodId === SHIPPING_METHODS.BOPIS
      && this.transaction?.availableShippingOptions[DeliveryOptions.CLICK_AND_COLLECT]
    ) {
      this.deliveryOption = DeliveryOptions.CLICK_AND_COLLECT;
      return;
    }

    if (
      [SHIPPING_METHODS.LOCAL_COLLECTION, SHIPPING_METHODS.STORE_COLLECTION].includes(selectedShippingMethodId)
      && this.transactionRepository?.availableDeliveryOptions[DeliveryOptions.UPS_STORE_PICK_UP]
    ) {
      this.deliveryOption = DeliveryOptions.UPS_STORE_PICK_UP;
      return;
    }

    if (
      selectedShippingMethodId === SHIPPING_METHODS.COURIER
      && this.transaction?.availableShippingOptions[DeliveryOptions.SAME_DAY_DELIVERY]
    ) {
      this.deliveryOption = DeliveryOptions.SAME_DAY_DELIVERY;
      return;
    }

    if (
      selectedShippingMethodId === SHIPPING_METHODS.STANDARD
      && this.transaction.shippingAddress?.attributes[FIELD_TYPE.ADDRESS_TYPE] === ADDRESS_TYPE_ID.PACKSTATION
    ) {
      this.deliveryOption = DeliveryOptions.PACKSTATION;
      return;
    }

    if (selectedShippingMethodId === SHIPPING_METHODS.ELECTRONIC_SHIPPING) {
      this.deliveryOption = DeliveryOptions.ELECTRONIC_DELIVERY;
      return;
    }

    const deliveryOption = this.transaction?.getDeliveryOption(selectedShippingMethodId);
    if (deliveryOption) {
      this.deliveryOption = deliveryOption.name;
      return;
    }

    // eslint-disable-next-line prefer-destructuring
    this.deliveryOption = DeliveryOptions.HOME_DELIVERY;
  };

  @action
  public selectDeliveryOption = async (deliveryOption: DeliveryOptions): Promise<void> => {
    // Clearing the billing address error notifications when the delivery option is changed
    this.notificationsCollection.clearNotifications(NOTIFICATION_GROUP.BILLING);
    if (deliveryOption === DeliveryOptions.AU_POST_COLLECTIONS && !this.shouldEnableAUPostDeliveryOption) {
      return;
    }
    const selectDeliveryOptionUseCase = new SelectDeliveryOptionUseCase(
      deliveryOption,
      this.inEditMode,
    );
    this.isDeliveryMethodLoading = true;
    await selectDeliveryOptionUseCase.execute();

    // observables should be set by action
    this.setIsDeliveryMethodLoading(false);
    this.setDeliveryOption(deliveryOption);
    this.setIsEditCartBtnLoading(false);

    this.analyticsRepository.selectDeliveryOptionEvent(deliveryOption);
  };

  @action
  handleNamedDeliveryDateChange = (_: string, value: string): void => {
    this.transaction.setNamedDeliveryDate(value);
  };

  @action
  handleNamedDeliveryTimeChange = (_: string, value: string): void => {
    this.transaction.setNamedDeliveryTime(value);
  };

  @computed
  get shouldShowARAlert(): boolean {
    return (
      this.transaction
      && this.transaction.cart.isAnyItemAutoReplenish
      && (this.deliveryOption === DeliveryOptions.CLICK_AND_COLLECT
        || this.deliveryOption === DeliveryOptions.UPS_STORE_PICK_UP
        || this.deliveryOption === DeliveryOptions.SAME_DAY_DELIVERY)
    );
  }

  @computed
  get showGiftWrapMessage(): boolean {
    const isClickAndCollect = this.config?.isClickAndCollect;
    const bopisGiftWrapEnabled = this.config?.bopisGiftWrapEnabled;
    return (!isClickAndCollect || !bopisGiftWrapEnabled) && !!this.transaction?.cart.hasGiftWrapItem;
  }

  @action
  public getCourierStoreHours = async (location?: AddressId | string): Promise<void> => {
    if (location) {
      const selectedAddress = this.shippingRepository.getAddressById(location);
      if (selectedAddress) {
        this.shippingRepository.formStore.setAddress(selectedAddress);
      }
    }
    const {
      formStore: {
        values: {
          [FIELD_TYPE.FIRST_LINE_ADDRESS]: firstLineAddress,
          [FIELD_TYPE.ZIPCODE]: zipcode,
        },
      },
    } = this.shippingRepository;

    const deliveryTabsDisabled = this.transaction?.deliveryTabsDisabled;
    const isSameDayDelivery = this.config.isSameDayDeliveryAvailable
      && (this.deliveryOption === DeliveryOptions.SAME_DAY_DELIVERY
      || this.transaction.availableShippingOptions[DeliveryOptions.SAME_DAY_DELIVERY]);

    if (isSameDayDelivery && !deliveryTabsDisabled) {
      if (firstLineAddress && zipcode) {
        const address = {
          [FIELD_TYPE.FIRST_LINE_ADDRESS]: firstLineAddress,
          [FIELD_TYPE.ZIPCODE]: zipcode,
        };
        await this.loadStoresService.loadCourierStoresByLocation(address);
      } else {
        await this.loadStoresService.loadCourierStoresByLocation(location);
      }
    }
  };

  @action
  public updateEstimatedDeliveryDate = async (addressId?: AddressId, zipCode?: string): Promise<void> => {
    const {
      formStore: {
        values: {
          [FIELD_TYPE.FIRST_LINE_ADDRESS]: firstLineAddress,
          [FIELD_TYPE.CITY]: city,
        },
      },
      getEstimatedDeliveryDate,
    } = this.shippingRepository;

    const isEstimatedDeliveryEligible = !this.transaction?.deliveryTabsDisabled && this.config.isEstimatedDeliveryDateEnabled;

    if (!isEstimatedDeliveryEligible) {
      return;
    }

    let estimatedDeliveryDateMapping: IFromEstimatedDeliveryMapping;

    if (zipCode) {
      const address = {
        city,
        firstLineAddress,
        zipcode: zipCode,
      };
      estimatedDeliveryDateMapping = await getEstimatedDeliveryDate(address);
    } else {
      estimatedDeliveryDateMapping = await getEstimatedDeliveryDate(addressId || this.transaction?.shippingAddress?.id);
    }

    if (estimatedDeliveryDateMapping) {
      this.transactionRepository.updateEstimatedDeliveryDateResponse(estimatedDeliveryDateMapping);
    }
  };

  @action
  public saveCourierShippingAddress = async (
    isBillingSameAsShipping: boolean,
    withUpsValidation: boolean,
  ): Promise<void> => {
    const useCase = new SaveCourierAddressUseCase(isBillingSameAsShipping, withUpsValidation);
    await useCase.execute();

    this.shippingRepository.formStore.setAddress(this.transaction.shippingAddress);
    await this.getCourierStoreHours();
  };

  @action
  public submitCourier = async (
    isBillingSameAsShipping: boolean,
    withUpsValidation: boolean,
  ): Promise<void> => {
    // submit only if there is no courrier error set
    const { courierStoresCollection } = this.courierStoresRepository;

    if (!courierStoresCollection.shouldShowCourierError && !this.shouldShowEditCart) {
      await this.saveCourierShippingAddress(isBillingSameAsShipping, withUpsValidation);
      const courierTimeRange = this.selectedCourierTime
        || courierStoresCollection.getCourierValsFromFirstOption();

      const courierData = courierStoresCollection.getCourierValsFromTimeRange(
        courierTimeRange,
      );

      const useCase = new SubmitCourierUseCase(
        courierData,
        courierStoresCollection.sortedDoorIds,
        this.dropoffNotes,
      );
      await useCase.execute();

      if (!this.courierStoresRepository.courierStoresCollection.isAddressUndeliverable) {
        this.stateRouter.changeCurrentRoute(PAYMENT.ROUTE);
      }
      this.setDeliveryStatus(Status.LOADED);
    }
  };

  @action
  handleTimeRangeChange = (_: string, value: string): void => {
    // prevent wait message from being set as selected
    if (!/\d/.test(value)) {
      return;
    }

    if (this._selectedCourierTime !== value) {
      this.analyticsRepository.postmatesTimeWindowSelected(value);
    }

    this._selectedCourierTime = value;
  };

  @action
  handleDropoffNotesChange = (e: React.FormEvent<HTMLTextAreaElement>): void => {
    this.dropoffNotes = e.currentTarget.value;
  };

  @action
  setRemoveUnavailableSelected = (removeUnavailableSelected: boolean): void => {
    this.removeUnavailableSelected = removeUnavailableSelected;
  };

  @action
  setIsEditCartBtnLoading = (isEditCartBtnLoading: boolean): void => {
    this.isEditCartBtnLoading = isEditCartBtnLoading;
  };

  @computed
  get selectedCourierTime(): string {
    if (this.courierStoresRepository.isInventoryLoading) {
      return '';
    }
    return this._selectedCourierTime;
  }

  @action
  public onEditCart = async (
    option: EDIT_CART_OPTION,
    isBillingSameAsShipping: boolean,
    withUpsValidation: boolean,
  ): Promise<void> => {
    if (option === EDIT_CART_OPTION.SHIP_ALL_ITEMS) {
      this.setIsEditCartBtnLoading(true);
      this.setRemoveUnavailableSelected(false);
      this.selectDeliveryOption(DeliveryOptions.HOME_DELIVERY);
      this.setDeliveryStatus(Status.LOADED);
    }
    if (option === EDIT_CART_OPTION.REMOVE_ITEMS) {
      const isNewBillingAddress = this.transaction?.billingAddress.isNewAddress;
      this.setRemoveUnavailableSelected(true);
      this.setIsEditCartBtnLoading(true);
      try {
        await this.submitCourier(isBillingSameAsShipping, withUpsValidation);
        if (!isNewBillingAddress) {
          await this.transactionRepository.loadTransaction();
        }
      } catch (error) {
        this.handleAddressSubmissionErrors(error);
      }
    }
  };

  @computed
  get cartItemsUnavailable(): CartItem[] {
    const { courierStoresCollection } = this.courierStoresRepository;
    return this.transaction?.cart?.cartItems?.filter(cartItem => {
      const item = courierStoresCollection?.courierStore?.inventory.find(
        inv => inv.skuBaseId === cartItem.skuBaseId,
      );
      return item && !item.isAvailable && !cartItem.isSampleItem;
    });
  }

  @computed
  get shouldShowEditCart(): boolean {
    return (
      this.cartItemsUnavailable
      && Boolean(this.cartItemsUnavailable.length)
      && !this.removeUnavailableSelected
      && this.deliveryStatus === Status.LOADING
    );
  }

  @computed
  get isOnlyGWPUnavailable(): boolean {
    return this.cartItemsUnavailable
    && (this.cartItemsUnavailable.every(item => item.isBopisGWPItem)
      || this.cartItemsUnavailable.every(item => item.isCourierGWPItem));
  }

  getPrice = (price: number): string => this.config && formatPrice(this.config, price);

  @action
  updateShippingMethod = async (storeType: number): Promise<void> => {
    let shippingMethodId;
    if (storeType === STORE_TYPE.STORE_COLLECTION) {
      shippingMethodId = SHIPPING_METHODS.STORE_COLLECTION;
    } else if (storeType === STORE_TYPE.LOCAL_COLLECTION) {
      shippingMethodId = SHIPPING_METHODS.LOCAL_COLLECTION;
    }
    await this.transactionRepository.setShippingMethod(shippingMethodId);
  };

  @computed
  private get isSalonCommissionVisible(): boolean {
    return this.config?.salonCommissionEnabled
      && this.deliveryOption === DeliveryOptions.HOME_DELIVERY;
  }

  @computed
  get isPaymentReady(): boolean {
    const isMissingAddress = this.transaction?.shippingAddress?.isNewAddress
      || !this.transaction?.isBillingAddressEligibleForCurrentPayment;
    const isMissingBopisInfo = !this.transaction?.isBillingAddressEligibleForCurrentPayment
      || !this.bopisStoresRepository?.bopisFormData.isValid;
    switch (this.deliveryOption) {
      case DeliveryOptions.HOME_DELIVERY:
      case DeliveryOptions.SAME_DAY_DELIVERY:
        if (isMissingAddress) {
          return false;
        }
        break;
      case DeliveryOptions.CLICK_AND_COLLECT:
        if (isMissingBopisInfo) {
          return false;
        }
        break;
      case DeliveryOptions.UPS_STORE_PICK_UP:
        if (isMissingBopisInfo) {
          return false;
        }
        break;
      default:
        return true;
    }
    return true;
  }
  
  @action
  setIsUserSignedIn = (isUserSignedIn: boolean): void => {
    this.isUserSignedIn = isUserSignedIn;
  };

  @action
  setIsDeliveryMethodLoading = (isDeliveryMethodLoading: boolean): void => {
    this.isDeliveryMethodLoading = isDeliveryMethodLoading;
  };

  @action
  setDeliveryOption = (deliveryOption: DeliveryOptions): void => {
    this.deliveryOption = deliveryOption;
  };

  onEditAddress = async (): Promise<void> => {

    if (!this.isUserSignedIn || !this.transaction || this.transaction.shippingAddress.isNewAddress) {
      setTimeout(() => {
        scrollToTargets(FORM_ERROR_SELECTORS[SHIPPING_FORM_ERROR]);
      }, TIMEOUT_INTERVAL.HUNDRED);
      return;
    }

    this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE, {}, { inEditMode: 1 });

    const useCase = new EditShippingAddressUseCase(
      this.transaction.shippingAddress.id,
      this.transaction.isSameAsShipping,
    );

    await useCase.execute();

    setTimeout(() => {
      scrollToTargets(FORM_ERROR_SELECTORS[SHIPPING_FORM_ERROR_SELECTED]);
    }, TIMEOUT_INTERVAL.HUNDRED);
  };
}

export default DeliveryScreenViewController;
